import * as custom from './marketplace-custom-config.js';
import defaultLocationSearches from './default-location-searches';
import { defaultMCC, stripePublishableKey, stripeCountryDetails } from './stripe-config';
import { currencyConfiguration } from './currency-config';

const env = process.env.REACT_APP_ENV;
const dev = process.env.REACT_APP_ENV === 'development';
const lang = process.env.REACT_APP_LANG;

// If you want to change the language, remember to also change the
// locale data and the messages in the app.js file.
const locale = lang || 'pl';

const i18n = {
  /*
    0: Sunday
    1: Monday
    ...
    6: Saturday
  */
  firstDayOfWeek: 1,
};

// Should search results be ordered by distance to origin.
// NOTE: If this is set to true add parameter 'origin' to every location in default-location-searches.js
// Without the 'origin' parameter, search will not work correctly
// NOTE: Keyword search and ordering search results by distance can't be used at the same time. You can turn keyword
// search off by changing the keywordFilterConfig parameter active to false in marketplace-custom-config.js
const sortSearchByDistance = false;

// API supports custom processes to be used in booking process.
// We need to specify it when we are initiating a new order
// (or fetching price information by calling 'initiateSpeculative' endpoint).
//
// In a way, 'processAlias' defines which transaction process (or processes)
// this particular web application is able to handle.
const bookingProcessAlias = 'preauth-nightly-booking/release-1';

// The transaction line item code for the main unit type in bookings.
//
// Possible values: ['line-item/night', 'line-item/day', 'line-item/units';]
//
// Note: translations will use different translation keys for night, day or unit
// depending on the value chosen.
const bookingUnitType = 'day';

// Should the application fetch available time slots (currently defined as
// start and end dates) to be shown on listing page.
// const enableAvailability = process.env.REACT_APP_AVAILABILITY_ENABLED === 'true';
const enableAvailability = true;

// A maximum number of days forwards during which a booking can be made.
// This is limited due to Stripe holding funds up to 90 days from the
// moment they are charged. Also note that available time slots can only
// be fetched for 180 days in the future.
const dayCountAvailableForBooking = 170;

// To pass environment variables to the client app in the build
// script, react-scripts (and the sharetribe-scripts fork of
// react-scripts) require using the REACT_APP_ prefix to avoid
// exposing server secrets to the client side.
const sdkClientId = process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID;
const sdkBaseUrl = process.env.REACT_APP_SHARETRIBE_SDK_BASE_URL;
const sdkTransitVerbose = process.env.REACT_APP_SHARETRIBE_SDK_TRANSIT_VERBOSE === 'true';

const currency = process.env.REACT_APP_SHARETRIBE_MARKETPLACE_CURRENCY;

// Currency formatting options.
// See: https://github.com/yahoo/react-intl/wiki/API#formatnumber
const currencyConfig = currencyConfiguration(currency);

// Listing minimum price in currency sub units, e.g. cents.
// 0 means no restriction to the price
const listingMinimumPriceSubUnits = 0;

// Sentry DSN (Data Source Name), a client key for authenticating calls to Sentry
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
const sentryEnv = process.env.REACT_APP_SENTRY_ENVIRONMENT;

// If webapp is using SSL (i.e. it's behind 'https' protocol)
const usingSSL = process.env.REACT_APP_SHARETRIBE_USING_SSL === 'true';

// Address information is used in SEO schema for Organization (http://schema.org/PostalAddress)
const addressCountry = 'PL';
const addressRegion = 'Warsaw';
const postalCode = '00100';
const streetAddress = 'Geodezyjna 74';

// Canonical root url is needed in social media sharing and SEO optimization purposes.
const canonicalRootURL = process.env.REACT_APP_CANONICAL_ROOT_URL;

// Site title is needed in meta tags (bots and social media sharing reads those)
const siteTitle = 'Decathlon Rent';

// Twitter handle is needed in meta tags (twitter:site). Start it with '@' character
const siteTwitterHandle = null;

// Instagram page is used in SEO schema (http://schema.org/Organization)
const siteInstagramPage = null;

// Facebook page is used in SEO schema (http://schema.org/Organization)
const siteFB = [
  {countryId: 'pl', link: 'https://www.facebook.com/decathlonrent/'},
  {countryId: 'hu', link: 'https://www.facebook.com/decathlonmagyarorszag'},
  {countryId: 'cz', link: 'https://www.facebook.com/Decathlon.cz/?locale=cs_CZ'},
  {countryId: 'sk', link: 'https://www.facebook.com/decathlonslovensko/'}
];

const langIdForCountryId = [
  {countryId: 'cz', langId: 'cs', countryLimit: [ 'PL', 'HU', 'SK', 'CZ']}, 
  {countryId: 'ee', langId: 'et', countryLimit: [ 'PL', 'HU', 'SK', 'CZ']},
  {countryId: 'pl', langId: 'pl', countryLimit: [ 'PL', 'HU', 'SK', 'CZ']},
  {countryId: 'hu', langId: 'hu', countryLimit: [ 'PL', 'HU', 'SK', 'CZ']},
  {countryId: 'lt', langId: 'lt', countryLimit: [ 'PL', 'HU', 'SK', 'CZ']},
  {countryId: 'lv', langId: 'lv', countryLimit: [ 'PL', 'HU', 'SK', 'CZ']},
  {countryId: 'en', langId: 'en', countryLimit: [ 'PL', 'HU', 'SK', 'CZ']},
  {countryId: 'sk', langId: 'sk', countryLimit: [ 'PL', 'HU', 'SK', 'CZ']},
]

const defaultAddress = {
  hu: { address: 'Magyarország', bounds: '48.583333,22.9,45.75,16.1' },
  pl: { address: 'Polska', bounds: '54.9054761,24.1458931,49.002025,14.1228641' },
  lt: { address: 'Lietuva', bounds: '57.33552157,25.64131462,52.83275719,20.89522087' },
  cz: { address: 'Česká republika', bounds: '52.54904037,18.47034983,47.1965123,12.93324045' },
  lv: { address: 'Rīga, Latvija', bounds: '57.23945983,24.49227362,56.60993069,23.85094671' },
  ee: { address: 'Tallinn, Eesti', bounds: '59.64198683,25.05528371,59.2060887,24.46751516' },
  sk: { address: 'Slovensko', bounds: '49.632178,22.574582,47.425603,16.937156' },
};

const fb = siteFB.find(({countryId}) => countryId === process.env.REACT_APP_COUNTRY_ID );
const siteFacebookPage = fb ? fb.link : siteFB[0].link;

// Facebook counts shares with app or page associated by this id
// Currently it is unset, but you can read more about fb:app_id from
// https://developers.facebook.com/docs/sharing/webmasters#basic
// You should create one to track social sharing in Facebook
const facebookAppId = null;

const maps = {
  googleMapsAPIKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,

  // The location search input can be configured to show default
  // searches when the user focuses on the input and hasn't yet typed
  // anything. This reduces typing and avoids too many Geolocation API
  // calls for common searches.
  search: {
    // When enabled, the first suggestion is "Current location" that
    // uses the browser Geolocation API to query the user's current
    // location.
    // suggestCurrentLocation: process.env.REACT_APP_DEFAULT_SEARCHES_ENABLED === 'true',
    suggestCurrentLocation: false,

    // Distance in meters for calculating the bounding box around the
    // current location.
    currentLocationBoundsDistance: 1000,

    // Example location can be edited in the
    // `default-location-searches.js` file.
    defaults:
      process.env.REACT_APP_DEFAULT_SEARCHES_ENABLED === 'true' ? defaultLocationSearches : [],

    // Limit location autocomplete to a one or more countries
    // using ISO 3166 alpha 2 country codes separated by commas.
    // If you want to limit the autocomplete, uncomment this value:
    countryLimit: langIdForCountryId.find(c => c.countryId === process.env.REACT_APP_COUNTRY_ID).countryLimit,
    countryBounds: defaultAddress[process.env.REACT_APP_COUNTRY_ID],
    defaultBounds: defaultAddress,
  },

  // When fuzzy locations are enabled, coordinates on maps are
  // obfuscated randomly around the actual location.
  //
  // NOTE: This only hides the locations in the UI level, the actual
  // coordinates are still accessible in the HTTP requests and the
  // Redux store.
  fuzzy: {
    enabled: false,

    // Amount of maximum offset in meters that is applied to obfuscate
    // the original coordinates. The actual value is random, but the
    // obfuscated coordinates are withing a circle that has the same
    // radius as the offset.
    offset: 500,

    // Default zoom level when showing a single circle on a Map. Should
    // be small enough so the whole circle fits in.
    defaultZoomLevel: 13,

    // Color of the circle on the Map component.
    circleColor: '#c0392b',
  },

  // Custom marker image to use in the Map component.
  //
  // NOTE: Not used if fuzzy locations are enabled.
  customMarker: {
    enabled: false,

    // Publicly accessible URL for the custom marker image.
    //
    // The easiest place is /public/static/icons/ folder, but then the
    // marker image is not available while developing through
    // localhost.
    url: encodeURI(`${canonicalRootURL}/static/icons/map-marker-32x32.png`),

    // Dimensions of the marker image.
    width: 32,
    height: 32,
  },
};

const countryId = ['pl', 'cz', 'hu', 'lv', 'lt', 'ee', 'sk'];

const countryNames = [
  { countryId: 'pl', country: 'ConsoleTransactionPage.countryNames.PL' },
  { countryId: 'hu', country: 'ConsoleTransactionPage.countryNames.HU' },
  { countryId: 'cz', country: 'ConsoleTransactionPage.countryNames.CZ' },
  // { countryId: 'lt', country: 'ConsoleTransactionPage.countryNames.LT' },
  // { countryId: 'lv', country: 'ConsoleTransactionPage.countryNames.LV' },
  // { countryId: 'ee', country: 'ConsoleTransactionPage.countryNames.EE' },
  { countryId: 'sk', country: 'ConsoleTransactionPage.countryNames.SK' },
];

const permissions = [
  { name: 'owner', value: 'owner' },
  { name: 'employee', value: 'employee' },
];

const categories = [
  {
    countries: ['cz', 'sk', 'pl'],
    name: 'CategoryBar.winter',
    subcategories: [
      { name: 'AlpineSkiingSearchPage', translation: "FiltersConfig.wintersportsAlpineskiing", countries: ['pl', 'cz', 'hu', 'sk'] },
      { name: 'WintersportsCrosscountrySearchPage', translation: "FiltersConfig.wintersportsCrosscountry", countries: ['cz'] },
      { name: 'SkiTouringSearchPage', translation: "FiltersConfig.wintersportsSkitouring", countries: ['sk'] },
      { name: 'WintersportsSnowboardSearchPage', translation: "FiltersConfig.wintersportsSnowboard", countries: ['pl', 'cz', 'hu', 'sk'] },
      { name: 'SeasonalSkiSearchPage', translation: "FiltersConfig.wintersportsSeasonalSki", countries: ['cz', 'sk'] },
      { name: 'WintersportsOtherSearchPage', translation: "FiltersConfig.wintersportsOther", countries: ['cz'] },
      { name: 'TourismCramponsSearchPage', translation: "FiltersConfig.tourismCrampons", countries: ['pl', 'cz', 'sk'] },
      { name: 'WintersportsCrosscountrySearchPage', translation: "FiltersConfig.wintersportsCrosscountry", countries: ['pl'] },
      { name: 'TourismSnowshoesSearchPage', translation: "FiltersConfig.tourismSnowshoes", countries: ['pl', 'cz'] },
      { name: 'TourismAxeSearchPage', translation: "FiltersConfig.tourismAxe", countries: ['pl'] },
    ],
  },
  {
    countries: ['cz'],
    name: 'CategoryBar.fitness',
    subcategories: [
      { name: 'FitnessCardioSearchPage', translation: "FiltersConfig.fitnessCardio", countries: ['cz', 'sk', 'lv', 'ee'] },
    ],
  },
  {
    countries: ['sk', 'cz'],
    name: 'CategoryBar.tourism',
    subcategories: [
      { name: 'CampingRoofTentSearchPage', translation: "FiltersConfig.campingRoofTent", countries: ['cz', 'sk', 'hu'] },
      { name: 'TourismTentSearchPage', translation: "FiltersConfig.tourismTent", countries: ['cz'] },
      { name: 'CampingTentSearchPage', translation: "FiltersConfig.campingTent", countries: ['pl', 'cz', 'sk', 'hu', 'lt', 'lv', 'ee'] },
      { name: 'CampingAccessoriesSearchPage', translation: "FiltersConfig.campingAccessories", countries: ['pl', 'cz', 'hu', 'lv', 'ee'] },
      { name: 'TourismCarrierSearchPage', translation: "FiltersConfig.tourismCarrier", countries: ['pl', 'cz', 'sk', 'lv', 'ee'] },
      // { name: 'TourismSticksSearchPage', translation: "FiltersConfig.tourismSticks" },
    ],
  },
  {
    countries: ['pl', 'cz', 'sk', 'hu'],
    name: 'CategoryBar.bikes',
    subcategories: [
      { name: 'GravelBikesSearchPage', translation: "FiltersConfig.bikesGravel", countries: ['pl', 'cz', 'sk', 'hu', 'lt'] },
      { name: 'ElectricBikesSearchPage', translation: "FiltersConfig.bikesElectric", countries: ['pl', 'cz', 'sk', 'hu', 'lt'] },
      { name: 'RoadBikesSearchPage', translation: "FiltersConfig.bikesRoad", countries: ['pl', 'cz', 'sk', 'hu'] },
      { name: 'MountainBikesSearchPage', translation: "FiltersConfig.bikesMountain", countries: ['pl', 'cz', 'hu', 'lt', 'lv', 'ee'] },
      { name: 'TrekkingBikesSearchPage', translation: "FiltersConfig.bikesTrekking", countries: ['pl', 'cz', 'hu'] },
      { name: 'BikesTrailerSearchPage', translation: "FiltersConfig.bikesTrailer", countries: ['pl', 'sk'] },
      { name: 'BikesTreadmillSearchPage', translation: "FiltersConfig.bikesTreadmill", countries: ['pl', 'cz'] },
      { name: 'BikesSeatSearchPage', translation: "FiltersConfig.bikesSeat", countries: ['pl'] },
      { name: 'TransportBikesSearchPage', translation: "FiltersConfig.transportBikes", countries: ['pl'] },
      // { name: 'BikesCitySearchPage', translation: "FiltersConfig.bikesCity" },
      { name: 'BikesAccessoriesSearchPage', translation: "FiltersConfig.bikesAccessories", countries: ['sk']  },
    ],
  },
  {
    countries: ['pl', 'hu'],
    name: 'CategoryBar.tourism',
    subcategories: [
      { name: 'CampingRoofTentSearchPage', translation: "FiltersConfig.campingRoofTent", countries: ['cz', 'sk', 'hu'] },
      { name: 'TourismTentSearchPage', translation: "FiltersConfig.tourismTent", countries: ['cz'] },
      { name: 'CampingTentSearchPage', translation: "FiltersConfig.campingTent", countries: ['pl', 'cz', 'sk', 'hu', 'lt', 'lv', 'ee'] },
      { name: 'CampingAccessoriesSearchPage', translation: "FiltersConfig.campingAccessories", countries: ['pl', 'cz', 'hu', 'lv', 'ee'] },
      { name: 'TourismCarrierSearchPage', translation: "FiltersConfig.tourismCarrier", countries: ['pl', 'cz', 'sk', 'lv', 'ee'] },
      // { name: 'TourismSticksSearchPage', translation: "FiltersConfig.tourismSticks" },
    ],
  },
  {
    countries: ['pl', 'cz', 'sk', 'hu'],
    name: 'CategoryBar.water',
    subcategories: [
      { name: 'WatersportsSupSearchPage', translation: "FiltersConfig.watersportsSup", countries: ['pl', 'cz', 'sk', 'hu', 'lt', 'lv', 'ee'] },
      { name: 'WatersportsKayakSearchPage', translation: "FiltersConfig.watersportsKayak", countries: ['pl', 'cz', 'hu', 'lv', 'ee', 'sk'] },
      { name: 'WatersportsLifejacketSearchPage', translation: "FiltersConfig.watersportsLifejacket", countries: ['pl'] },
      { name: 'WatersportsAccessoriesSearchPage', translation: "FiltersConfig.watersportsAccessories", countries: ['pl', 'cz', 'sk'] },
    ],
  },
  {
    countries: ['hu'],
    name: 'CategoryBar.winter',
    subcategories: [
      { name: 'AlpineSkiingSearchPage', translation: "FiltersConfig.wintersportsAlpineskiing", countries: ['pl', 'cz', 'hu', 'sk'] },
      { name: 'WintersportsCrosscountrySearchPage', translation: "FiltersConfig.wintersportsCrosscountry", countries: ['cz'] },
      { name: 'SkiTouringSearchPage', translation: "FiltersConfig.wintersportsSkitouring", countries: ['sk'] },
      { name: 'WintersportsSnowboardSearchPage', translation: "FiltersConfig.wintersportsSnowboard", countries: ['pl', 'cz', 'hu', 'sk'] },
      { name: 'SeasonalSkiSearchPage', translation: "FiltersConfig.wintersportsSeasonalSki", countries: ['cz', 'sk'] },
      { name: 'WintersportsOtherSearchPage', translation: "FiltersConfig.wintersportsOther", countries: ['cz'] },
      { name: 'TourismCramponsSearchPage', translation: "FiltersConfig.tourismCrampons", countries: ['pl', 'cz', 'sk'] },
      { name: 'WintersportsCrosscountrySearchPage', translation: "FiltersConfig.wintersportsCrosscountry", countries: ['pl'] },
      { name: 'TourismSnowshoesSearchPage', translation: "FiltersConfig.tourismSnowshoes", countries: ['pl', 'cz'] },
      { name: 'TourismAxeSearchPage', translation: "FiltersConfig.tourismAxe", countries: ['pl'] },
    ],
  },
  // {
  //   countries: ['pl', 'cz', 'sk', 'hu', 'lt', 'lv', 'ee'],
  //   name: 'CategoryBar.water',
  //   subcategories: [
  //     { name: 'WatersportsSupSearchPage', translation: "FiltersConfig.watersportsSup", countries: ['pl', 'cz', 'sk', 'hu', 'lt', 'lv', 'ee'] },
  //     { name: 'WatersportsKayakSearchPage', translation: "FiltersConfig.watersportsKayak", countries: ['pl', 'cz', 'hu', 'lv', 'ee', 'sk'] },
  //     { name: 'WatersportsLifejacketSearchPage', translation: "FiltersConfig.watersportsLifejacket", countries: ['pl'] },
  //     { name: 'WatersportsAccessoriesSearchPage', translation: "FiltersConfig.watersportsAccessories", countries: ['pl', 'cz', 'sk'] },
  //   ],
  // },
];

const countries = [
  {
    "code": "PL",
    "name": "Polska",
    "eu": true
  },
  {
    "code": "AF",
    "name": "Afganistan"
  },
  {
    "code": "AX",
    "name": "Wyspy Alandzkie"
  },
  {
    "code": "AL",
    "name": "Albania"
  },
  {
    "code": "DZ",
    "name": "Algieria"
  },
  {
    "code": "AD",
    "name": "Andora"
  },
  {
    "code": "AO",
    "name": "Angola"
  },
  {
    "code": "AI",
    "name": "Anguilla"
  },
  {
    "code": "AQ",
    "name": "Antarktyda"
  },
  {
    "code": "AG",
    "name": "Antigua i Barbuda"
  },
  {
    "code": "AN",
    "name": "Antyle Holenderskie"
  },
  {
    "code": "SA",
    "name": "Arabia Saudyjska"
  },
  {
    "code": "AR",
    "name": "Argentyna"
  },
  {
    "code": "AM",
    "name": "Armenia"
  },
  {
    "code": "AW",
    "name": "Aruba"
  },
  {
    "code": "AU",
    "name": "Australia"
  },
  {
    "code": "AT",
    "name": "Austria",
    "eu": true
  },
  {
    "code": "AZ",
    "name": "Azerbejdżan"
  },
  {
    "code": "BS",
    "name": "Bahamy"
  },
  {
    "code": "BH",
    "name": "Bahrajn"
  },
  {
    "code": "BD",
    "name": "Bangladesz"
  },
  {
    "code": "BB",
    "name": "Barbados"
  },
  {
    "code": "BE",
    "name": "Belgia",
    "eu": true
  },
  {
    "code": "BZ",
    "name": "Belize"
  },
  {
    "code": "BJ",
    "name": "Benin"
  },
  {
    "code": "BM",
    "name": "Bermudy"
  },
  {
    "code": "BT",
    "name": "Bhutan"
  },
  {
    "code": "BY",
    "name": "Białoruś"
  },
  {
    "code": "BO",
    "name": "Boliwia"
  },
  {
    "code": "BQ",
    "name": "Karaibska Holandia (Bonaire, Sint Eustatius i Saba)"
  },
  {
    "code": "BA",
    "name": "Bośnia i Hercegowina"
  },
  {
    "code": "BW",
    "name": "Botswana"
  },
  {
    "code": "BR",
    "name": "Brazylia"
  },
  {
    "code": "BN",
    "name": "Brunei"
  },
  {
    "code": "IO",
    "name": "Brytyjskie Terytorium Oceanu Indyjskiego"
  },
  {
    "code": "BG",
    "name": "Bułgaria",
    "eu": true
  },
  {
    "code": "BF",
    "name": "Burkina Faso"
  },
  {
    "code": "BI",
    "name": "Burundi"
  },
  {
    "code": "XC",
    "name": "Kod niestandardowy (XC)"
  },
  {
    "code": "CL",
    "name": "Chile"
  },
  {
    "code": "CN",
    "name": "Chiny"
  },
  {
    "code": "HR",
    "name": "Chorwacja",
    "eu": true
  },
  {
    "code": "CW",
    "name": "Curaçao"
  },
  {
    "code": "CY",
    "name": "Cypr",
    "eu": true
  },
  {
    "code": "TD",
    "name": "Czad"
  },
  {
    "code": "ME",
    "name": "Czarnogóra"
  },
  {
    "code": "DK",
    "name": "Dania",
    "eu": true
  },
  {
    "code": "DM",
    "name": "Dominika"
  },
  {
    "code": "DO",
    "name": "Dominikana"
  },
  {
    "code": "DJ",
    "name": "Dżibuti"
  },
  {
    "code": "EG",
    "name": "Egipt"
  },
  {
    "code": "EC",
    "name": "Ekwador"
  },
  {
    "code": "ER",
    "name": "Erytrea"
  },
  {
    "code": "EE",
    "name": "Estonia",
    "eu": true
  },
  {
    "code": "ET",
    "name": "Etiopia"
  },
  {
    "code": "FK",
    "name": "Falklandy"
  },
  {
    "code": "FJ",
    "name": "Fidżi"
  },
  {
    "code": "PH",
    "name": "Filipiny"
  },
  {
    "code": "FI",
    "name": "Finlandia",
    "eu": true
  },
  {
    "code": "FR",
    "name": "Francja",
    "eu": true
  },
  {
    "code": "TF",
    "name": "Francuskie Terytoria Południowe i Antarktyczne"
  },
  {
    "code": "GA",
    "name": "Gabon"
  },
  {
    "code": "GM",
    "name": "Gambia"
  },
  {
    "code": "GH",
    "name": "Ghana"
  },
  {
    "code": "GI",
    "name": "Gibraltar"
  },
  {
    "code": "GR",
    "name": "Grecja",
    "eu": true
  },
  {
    "code": "GD",
    "name": "Grenada"
  },
  {
    "code": "GL",
    "name": "Grenlandia"
  },
  {
    "code": "GE",
    "name": "Gruzja"
  },
  {
    "code": "GU",
    "name": "Guam"
  },
  {
    "code": "GG",
    "name": "Guernsey"
  },
  {
    "code": "GY",
    "name": "Gujana"
  },
  {
    "code": "GF",
    "name": "Gujana Francuska"
  },
  {
    "code": "GP",
    "name": "Gwadelupa"
  },
  {
    "code": "GT",
    "name": "Gwatemala"
  },
  {
    "code": "GN",
    "name": "Gwinea"
  },
  {
    "code": "GQ",
    "name": "Gwinea Równikowa"
  },
  {
    "code": "GW",
    "name": "Gwinea Bissau"
  },
  {
    "code": "HT",
    "name": "Haiti"
  },
  {
    "code": "ES",
    "name": "Hiszpania",
    "eu": true
  },
  {
    "code": "HN",
    "name": "Honduras"
  },
  {
    "code": "HK",
    "name": "Hongkong"
  },
  {
    "code": "IN",
    "name": "Indie"
  },
  {
    "code": "ID",
    "name": "Indonezja"
  },
  {
    "code": "IQ",
    "name": "Irak"
  },
  {
    "code": "IR",
    "name": "Iran"
  },
  {
    "code": "IE",
    "name": "Irlandia",
    "eu": true
  },
  {
    "code": "IS",
    "name": "Islandia"
  },
  {
    "code": "IL",
    "name": "Izrael"
  },
  {
    "code": "JM",
    "name": "Jamajka"
  },
  {
    "code": "JP",
    "name": "Japonia"
  },
  {
    "code": "YE",
    "name": "Jemen"
  },
  {
    "code": "JE",
    "name": "Jersey"
  },
  {
    "code": "JO",
    "name": "Jordania"
  },
  {
    "code": "KY",
    "name": "Kajmany"
  },
  {
    "code": "KH",
    "name": "Kambodża"
  },
  {
    "code": "CM",
    "name": "Kamerun"
  },
  {
    "code": "CA",
    "name": "Kanada"
  },
  {
    "code": "QA",
    "name": "Katar"
  },
  {
    "code": "KZ",
    "name": "Kazachstan"
  },
  {
    "code": "KE",
    "name": "Kenia"
  },
  {
    "code": "KG",
    "name": "Kirgistan"
  },
  {
    "code": "KI",
    "name": "Kiribati"
  },
  {
    "code": "CO",
    "name": "Kolumbia"
  },
  {
    "code": "KM",
    "name": "Komory"
  },
  {
    "code": "CG",
    "name": "Kongo (Republika Konga)"
  },
  {
    "code": "CD",
    "name": "Kongo (Demokratyczna Republika Konga)"
  },
  {
    "code": "KP",
    "name": "Korea Północna"
  },
  {
    "code": "XK",
    "name": "Kosowo"
  },
  {
    "code": "CR",
    "name": "Kostaryka"
  },
  {
    "code": "CU",
    "name": "Kuba"
  },
  {
    "code": "KW",
    "name": "Kuwejt"
  },
  {
    "code": "LA",
    "name": "Laos"
  },
  {
    "code": "LS",
    "name": "Lesotho"
  },
  {
    "code": "LB",
    "name": "Liban"
  },
  {
    "code": "LR",
    "name": "Liberia"
  },
  {
    "code": "LY",
    "name": "Libia"
  },
  {
    "code": "LI",
    "name": "Liechtenstein"
  },
  {
    "code": "LT",
    "name": "Litwa",
    "eu": true
  },
  {
    "code": "LV",
    "name": "Łotwa",
    "eu": true
  },
  {
    "code": "LU",
    "name": "Luksemburg",
    "eu": true
  },
  {
    "code": "MK",
    "name": "Macedonia Północna"
  },
  {
    "code": "MG",
    "name": "Madagaskar"
  },
  {
    "code": "YT",
    "name": "Majotta"
  },
  {
    "code": "MO",
    "name": "Makau"
  },
  {
    "code": "MW",
    "name": "Malawi"
  },
  {
    "code": "MV",
    "name": "Malediwy"
  },
  {
    "code": "MY",
    "name": "Malezja"
  },
  {
    "code": "ML",
    "name": "Mali"
  },
  {
    "code": "MT",
    "name": "Malta",
    "eu": true
  },
  {
    "code": "MP",
    "name": "Mariany Północne"
  },
  {
    "code": "MA",
    "name": "Maroko"
  },
  {
    "code": "MQ",
    "name": "Martynika"
  },
  {
    "code": "MR",
    "name": "Mauretania"
  },
  {
    "code": "MU",
    "name": "Mauritius"
  },
  {
    "code": "MX",
    "name": "Meksyk"
  },
  {
    "code": "XL",
    "name": "Kod niestandardowy (XL)"
  },
  {
    "code": "FM",
    "name": "Mikronezja"
  },
  {
    "code": "UM",
    "name": "Dalekie Wyspy Mniejsze Stanów Zjednoczonych"
  },
  {
    "code": "MD",
    "name": "Mołdawia"
  },
  {
    "code": "MC",
    "name": "Monako"
  },
  {
    "code": "MN",
    "name": "Mongolia"
  },
  {
    "code": "MS",
    "name": "Montserrat"
  },
  {
    "code": "MZ",
    "name": "Mozambik"
  },
  {
    "code": "MM",
    "name": "Mjanma (Birma)"
  },
  {
    "code": "NA",
    "name": "Namibia"
  },
  {
    "code": "NR",
    "name": "Nauru"
  },
  {
    "code": "NP",
    "name": "Nepal"
  },
  {
    "code": "NL",
    "name": "Niderlandy",
    "eu": true
  },
  {
    "code": "DE",
    "name": "Niemcy",
    "eu": true
  },
  {
    "code": "NE",
    "name": "Niger"
  },
  {
    "code": "NG",
    "name": "Nigeria"
  },
  {
    "code": "NI",
    "name": "Nikaragua"
  },
  {
    "code": "NU",
    "name": "Niue"
  },
  {
    "code": "NF",
    "name": "Norfolk"
  },
  {
    "code": "NO",
    "name": "Norwegia"
  },
  {
    "code": "NC",
    "name": "Nowa Kaledonia"
  },
  {
    "code": "NZ",
    "name": "Nowa Zelandia"
  },
  {
    "code": "PS",
    "name": "Palestyna"
  },
  {
    "code": "OM",
    "name": "Oman"
  },
  {
    "code": "PK",
    "name": "Pakistan"
  },
  {
    "code": "PW",
    "name": "Palau"
  },
  {
    "code": "PA",
    "name": "Panama"
  },
  {
    "code": "PG",
    "name": "Papua-Nowa Gwinea"
  },
  {
    "code": "PY",
    "name": "Paragwaj"
  },
  {
    "code": "PE",
    "name": "Peru"
  },
  {
    "code": "PN",
    "name": "Pitcairn"
  },
  {
    "code": "PF",
    "name": "Polinezja Francuska"
  },
  {
    "code": "GS",
    "name": "Georgia Południowa i Sandwich Południowy"
  },
  {
    "code": "PT",
    "name": "Portugalia",
    "eu": true
  },
  {
    "code": "PR",
    "name": "Portoryko"
  },
  {
    "code": "CF",
    "name": "Republika Środkowoafrykańska"
  },
  {
    "code": "CZ",
    "name": "Czechy",
    "eu": true
  },
  {
    "code": "KR",
    "name": "Korea Południowa"
  },
  {
    "code": "ZA",
    "name": "Republika Południowej Afryki"
  },
  {
    "code": "RE",
    "name": "Reunion"
  },
  {
    "code": "RU",
    "name": "Rosja"
  },
  {
    "code": "RO",
    "name": "Rumunia",
    "eu": true
  },
  {
    "code": "RW",
    "name": "Rwanda"
  },
  {
    "code": "EH",
    "name": "Sahara Zachodnia"
  },
  {
    "code": "BL",
    "name": "Saint-Barthélemy"
  },
  {
    "code": "KN",
    "name": "Saint Kitts i Nevis"
  },
  {
    "code": "LC",
    "name": "Saint Lucia"
  },
  {
    "code": "MF",
    "name": "Saint-Martin (Francja)"
  },
  {
    "code": "VC",
    "name": "Saint Vincent i Grenadyny"
  },
  {
    "code": "SV",
    "name": "Salwador"
  },
  {
    "code": "WS",
    "name": "Samoa"
  },
  {
    "code": "AS",
    "name": "Samoa Amerykańskie"
  },
  {
    "code": "SM",
    "name": "San Marino"
  },
  {
    "code": "SN",
    "name": "Senegal"
  },
  {
    "code": "RS",
    "name": "Serbia"
  },
  {
    "code": "SC",
    "name": "Seszele"
  },
  {
    "code": "SL",
    "name": "Sierra Leone"
  },
  {
    "code": "SG",
    "name": "Singapur"
  },
  {
    "code": "SK",
    "name": "Słowacja",
    "eu": true
  },
  {
    "code": "SI",
    "name": "Słowenia",
    "eu": true
  },
  {
    "code": "SO",
    "name": "Somalia"
  },
  {
    "code": "LK",
    "name": "Sri Lanka"
  },
  {
    "code": "PM",
    "name": "Saint-Pierre i Miquelon"
  },
  {
    "code": "US",
    "name": "Stany Zjednoczone"
  },
  {
    "code": "SZ",
    "name": "Eswatini (Suazi)"
  },
  {
    "code": "SD",
    "name": "Sudan"
  },
  {
    "code": "SS",
    "name": "Sudan Południowy"
  },
  {
    "code": "SR",
    "name": "Surinam"
  },
  {
    "code": "SJ",
    "name": "Svalbard i Jan Mayen"
  },
  {
    "code": "SH",
    "name": "Wyspa Świętej Heleny, Wniebowstąpienia i Tristan da Cunha"
  },
  {
    "code": "SY",
    "name": "Syria"
  },
  {
    "code": "CH",
    "name": "Szwajcaria"
  },
  {
    "code": "SE",
    "name": "Szwecja",
    "eu": true
  },
  {
    "code": "TJ",
    "name": "Tadżykistan"
  },
  {
    "code": "TH",
    "name": "Tajlandia"
  },
  {
    "code": "TW",
    "name": "Tajwan"
  },
  {
    "code": "TZ",
    "name": "Tanzania"
  },
  {
    "code": "TG",
    "name": "Togo"
  },
  {
    "code": "TK",
    "name": "Tokelau"
  },
  {
    "code": "TO",
    "name": "Tonga"
  },
  {
    "code": "TT",
    "name": "Trynidad i Tobago"
  },
  {
    "code": "TN",
    "name": "Tunezja"
  },
  {
    "code": "TR",
    "name": "Turcja"
  },
  {
    "code": "TM",
    "name": "Turkmenistan"
  },
  {
    "code": "TV",
    "name": "Tuvalu"
  },
  {
    "code": "UG",
    "name": "Uganda"
  },
  {
    "code": "UA",
    "name": "Ukraina"
  },
  {
    "code": "UY",
    "name": "Urugwaj"
  },
  {
    "code": "UZ",
    "name": "Uzbekistan"
  },
  {
    "code": "VU",
    "name": "Vanuatu"
  },
  {
    "code": "WF",
    "name": "Wallis i Futuna"
  },
  {
    "code": "VA",
    "name": "Watykan"
  },
  {
    "code": "HU",
    "name": "Węgry",
    "eu": true
  },
  {
    "code": "VE",
    "name": "Wenezuela"
  },
  {
    "code": "GB",
    "name": "Wielka Brytania"
  },
  {
    "code": "VN",
    "name": "Wietnam"
  },
  {
    "code": "IT",
    "name": "Włochy",
    "eu": true
  },
  {
    "code": "TL",
    "name": "Timor Wschodni"
  },
  {
    "code": "CI",
    "name": "Wybrzeże Kości Słoniowej"
  },
  {
    "code": "BV",
    "name": "Wyspa Bouveta"
  },
  {
    "code": "CX",
    "name": "Wyspa Bożego Narodzenia"
  },
  {
    "code": "IM",
    "name": "Wyspa Man"
  },
  {
    "code": "SX",
    "name": "Sint Maarten (Królestwo Niderlandów)"
  },
  {
    "code": "CK",
    "name": "Wyspy Cooka"
  },
  {
    "code": "VI",
    "name": "Wyspy Dziewicze Stanów Zjednoczonych"
  },
  {
    "code": "VG",
    "name": "Brytyjskie Wyspy Dziewicze"
  },
  {
    "code": "HM",
    "name": "Wyspy Heard i McDonalda"
  },
  {
    "code": "CC",
    "name": "Wyspy Kokosowe (Keelinga)"
  },
  {
    "code": "MH",
    "name": "Wyspy Marshalla"
  },
  {
    "code": "FO",
    "name": "Wyspy Owcze"
  },
  {
    "code": "SB",
    "name": "Wyspy Salomona"
  },
  {
    "code": "ST",
    "name": "Wyspy Świętego Tomasza i Książęca"
  },
  {
    "code": "TC",
    "name": "Turks i Caicos"
  },
  {
    "code": "ZM",
    "name": "Zambia"
  },
  {
    "code": "CV",
    "name": "Zielony Przylądek"
  },
  {
    "code": "ZW",
    "name": "Zimbabwe"
  },
  {
    "code": "AE",
    "name": "Zjednoczone Emiraty Arabskie"
  },
  {
    "code": "XI",
    "name": "Irlandia Północna",
    "eu": true
  }
]

// NOTE: only expose configuration that should be visible in the
// client side, don't add any server secrets in this file.
const config = {
  env,
  dev,
  locale,
  bookingProcessAlias,
  bookingUnitType,
  enableAvailability,
  dayCountAvailableForBooking,
  i18n,
  sdk: {
    clientId: sdkClientId,
    baseUrl: sdkBaseUrl,
    transitVerbose: sdkTransitVerbose,
  },
  sortSearchByDistance,
  currency,
  currencyConfig,
  listingMinimumPriceSubUnits,
  stripe: {
    defaultMCC: defaultMCC,
    publishableKey: stripePublishableKey,
    supportedCountries: stripeCountryDetails,
  },
  canonicalRootURL,
  address: {
    addressCountry,
    addressRegion,
    postalCode,
    streetAddress,
  },
  siteTitle,
  siteFacebookPage,
  siteInstagramPage,
  siteTwitterHandle,
  facebookAppId,
  sentryDsn,
  sentryEnv,
  usingSSL,
  maps,
  custom,
  countryId,
  countryNames,
  permissions,
  langIdForCountryId,
  testProviderFilterOn: process.env.REACT_APP_COUNTRY_ID === 'cz',
  categories,
  countries: countries.sort((a, b) => {
    // 1. Polska ma najwyższy priorytet
    if (a.code === "PL") return -1;
    if (b.code === "PL") return 1;
  
    // 2. Kraje UE mają drugi priorytet
    if (a.eu && !b.eu) return -1;
    if (!a.eu && b.eu) return 1;
  
    // 3. Reszta krajów (i kraje wewnątrz grupy UE) sortowane alfabetycznie
    return a.name.localeCompare(b.name, 'pl');
  }),
};

export default config;
